import React, { useState } from "react";
import { Spin, theme, Table, Button, Tag, Input } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";

import api from "@/utils/api";
import { CompaniesResponse } from "@/utils/types/response/company";
import { SingleCompany } from "@/utils/types/response/company";
import { pageSizeOptions, defaultPageSize } from "@/utils/constants";

import FetchContainer from "@/components/FetchContainer/FetchContainer";

const getColumns = (
  companyName: string,
  setCompanyName: (name: string) => void,
): ColumnsType<SingleCompany> => {
  return [
    {
      title: "Name",
      dataIndex: "name",
      filterDropdown: () => (
        <div
          style={{ padding: 8, width: 200 }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Input.Search
            defaultValue={companyName}
            placeholder="Search..."
            onSearch={(value) => setCompanyName(value)}
          />
        </div>
      ),
    },
    {
      title: "Industry",
      dataIndex: "industry",
    },
    {
      title: "Endpoints",
      dataIndex: "endpoints",
      render: (endpoints) => (
        <Tag>{`${endpoints?.online ?? 0}/${endpoints?.total ?? 0}`}</Tag>
      ),
    },
    {
      title: "Action",
      dataIndex: "",
      width: 100,
      render: (_: unknown, company: SingleCompany) => (
        <Link
          href={`/${company.id}/dashboard`}
          passHref
          onClick={(e) => e.stopPropagation()}
        >
          <Button type="link" style={{ paddingLeft: 0 }}>
            Go to Portal
          </Button>
        </Link>
      ),
    },
  ];
};

const CompaniesLarge = () => {
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [companyName, setCompanyName] = useState("");

  const {
    data: companies,
    isPending,
    isError,
    refetch,
    isPlaceholderData,
  } = useQuery<CompaniesResponse>({
    queryKey: ["companies", page, pageSize, companyName],
    queryFn: () => api.company.getCompanies(page, pageSize, companyName),
    placeholderData: (prevData) => prevData,
  });

  return (
    <FetchContainer
      isPending={isPending}
      isError={isError}
      noData={false}
      retry={() => refetch()}
    >
      <Spin spinning={isPlaceholderData}>
        <Table
          style={{
            padding: 16,
            background: colorBgContainer,
            border: "1px solid #f0f0f0",
            borderRadius: "1rem",
          }}
          scroll={{ x: "max-content" }}
          rowKey={(record) => record.id}
          columns={getColumns(companyName, setCompanyName)}
          dataSource={companies?.data}
          pagination={{
            current: page,
            responsive: true,
            total: companies?.totalItems,
            pageSize: companies?.pageSize,
            showSizeChanger: true,
            pageSizeOptions: pageSizeOptions,
            onChange: (pageNumber) => {
              setPage(pageNumber);
            },
            onShowSizeChange: (_, pageSize) => {
              setPageSize(pageSize);
            },
            showTotal: (total, range) => {
              return `${range[0]}-${range[1]} of ${total} items`;
            },
          }}
        />
      </Spin>
    </FetchContainer>
  );
};

export default CompaniesLarge;
